import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Layers, Plus, Trash2, Copy, Share2, Sparkles, Flame, Moon, Brain, Target, Dumbbell } from 'lucide-react';
import { toast } from 'sonner';


const TEMPLATE_ICONS = {
  fat_loss: Flame,
  recovery: Target,
  sleep: Moon,
  anti_aging: Sparkles,
  cognitive: Brain,
  performance: Dumbbell
};

export default function Protocols() {
  const { user } = useOutletContext();
  const [protocols, setProtocols] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [peptides, setPeptides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('my-protocols');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    peptides: [],
    schedule: 'daily'
  });
  const [selectedPeptide, setSelectedPeptide] = useState({
    peptide_id: '',
    peptide_name: '',
    dose_amount: '',
    dose_unit: 'mcg',
    frequency: 'daily',
    timing: 'morning'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [protocolsRes, peptidesRes, templatesRes] = await Promise.all([
        fetch(`${API}/protocols`, { credentials: 'include' }),
        fetch(`${API}/peptides`, { credentials: 'include' }),
        fetch(`${API}/protocols/templates`, { credentials: 'include' })
      ]);

      if (protocolsRes.ok) setProtocols(await protocolsRes.json());
      if (peptidesRes.ok) setPeptides(await peptidesRes.json());
      if (templatesRes.ok) setTemplates(await templatesRes.json());
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePeptideSelect = (peptideId) => {
    const peptide = peptides.find(p => p.peptide_id === peptideId);
    setSelectedPeptide({
      ...selectedPeptide,
      peptide_id: peptideId,
      peptide_name: peptide?.name || ''
    });
  };

  const addPeptideToProtocol = () => {
    if (!selectedPeptide.peptide_id || !selectedPeptide.dose_amount) {
      toast.error('Please select a peptide and enter dose amount');
      return;
    }

    setFormData({
      ...formData,
      peptides: [...formData.peptides, { ...selectedPeptide }]
    });

    setSelectedPeptide({
      peptide_id: '',
      peptide_name: '',
      dose_amount: '',
      dose_unit: 'mcg',
      frequency: 'daily',
      timing: 'morning'
    });
  };

  const removePeptideFromProtocol = (index) => {
    setFormData({
      ...formData,
      peptides: formData.peptides.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || formData.peptides.length === 0) {
      toast.error('Please enter a name and add at least one peptide');
      return;
    }

    try {
      const response = await fetch(`${API}/protocols`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Protocol created');
        setShowDialog(false);
        setFormData({
          name: '',
          description: '',
          peptides: [],
          schedule: 'daily'
        });
        fetchData();
      } else {
        toast.error('Failed to create protocol');
      }
    } catch (error) {
      toast.error('Failed to create protocol');
    }
  };

  const handleDelete = async (protocolId) => {
    if (!confirm('Delete this protocol?')) return;

    try {
      const response = await fetch(`${API}/protocols/${protocolId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Protocol deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDuplicate = (protocol) => {
    setFormData({
      name: `${protocol.name} (Copy)`,
      description: protocol.description || '',
      peptides: [...protocol.peptides],
      schedule: protocol.schedule || 'daily'
    });
    setShowDialog(true);
  };

  const handleImportTemplate = async (templateName) => {
    try {
      const response = await fetch(`${API}/protocols/from-template/${encodeURIComponent(templateName)}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`Protocol "${templateName}" imported!`);
        setActiveTab('my-protocols');
        fetchData();
      } else {
        toast.error('Failed to import template');
      }
    } catch (error) {
      toast.error('Failed to import template');
    }
  };

  const handleShare = (protocol) => {
    const shareData = {
      name: protocol.name,
      peptides: protocol.peptides.map(p => ({
        name: p.peptide_name,
        dose: `${p.dose_amount} ${p.dose_unit}`,
        frequency: p.frequency,
        timing: p.timing
      }))
    };

    const shareText = `PeptideForge Protocol: ${protocol.name}\n\n${shareData.peptides.map(p => 
      `• ${p.name}: ${p.dose} (${p.frequency}, ${p.timing})`
    ).join('\n')}`;

    if (navigator.share) {
      navigator.share({
        title: `Protocol: ${protocol.name}`,
        text: shareText
      });
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Protocol copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-muted-foreground">Loading protocols...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="protocols-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">Protocols</h1>
          <p className="text-muted-foreground">Create and manage reusable peptide stacks</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-protocol-button">
              <Plus className="h-4 w-4 mr-2" />
              New Protocol
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Protocol</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Protocol Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Fat Loss Stack, Recovery Protocol"
                  required
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Describe the purpose and goals of this protocol..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Schedule</Label>
                <Select value={formData.schedule} onValueChange={(v) => setFormData({...formData, schedule: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="5on_2off">5 On / 2 Off</SelectItem>
                    <SelectItem value="eod">Every Other Day</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Added Peptides */}
              {formData.peptides.length > 0 && (
                <div>
                  <Label className="mb-2 block">Peptides in Protocol</Label>
                  <div className="space-y-2">
                    {formData.peptides.map((p, index) => (
                      <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 border">
                        <div>
                          <p className="font-medium">{p.peptide_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {p.dose_amount} {p.dose_unit} • {p.frequency} • {p.timing}
                          </p>
                        </div>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removePeptideFromProtocol(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Peptide Section */}
              <div className="border rounded-lg p-4 bg-secondary/20">
                <Label className="mb-3 block text-sm font-semibold">Add Peptide to Protocol</Label>
                <div className="space-y-3">
                  <Select value={selectedPeptide.peptide_id} onValueChange={handlePeptideSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select peptide" />
                    </SelectTrigger>
                    <SelectContent>
                      {peptides.map(peptide => (
                        <SelectItem key={peptide.peptide_id} value={peptide.peptide_id}>
                          {peptide.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      value={selectedPeptide.dose_amount}
                      onChange={(e) => setSelectedPeptide({...selectedPeptide, dose_amount: e.target.value})}
                      placeholder="Dose"
                    />
                    <Select value={selectedPeptide.dose_unit} onValueChange={(v) => setSelectedPeptide({...selectedPeptide, dose_unit: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mcg">mcg</SelectItem>
                        <SelectItem value="mg">mg</SelectItem>
                        <SelectItem value="IU">IU</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Select value={selectedPeptide.frequency} onValueChange={(v) => setSelectedPeptide({...selectedPeptide, frequency: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="eod">EOD</SelectItem>
                        <SelectItem value="2x_day">2x/day</SelectItem>
                        <SelectItem value="3x_week">3x/week</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={selectedPeptide.timing} onValueChange={(v) => setSelectedPeptide({...selectedPeptide, timing: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="morning">Morning</SelectItem>
                        <SelectItem value="pre_workout">Pre-Workout</SelectItem>
                        <SelectItem value="post_workout">Post-Workout</SelectItem>
                        <SelectItem value="evening">Evening</SelectItem>
                        <SelectItem value="before_bed">Before Bed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button type="button" variant="outline" className="w-full" onClick={addPeptideToProtocol}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Peptide
                  </Button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={formData.peptides.length === 0}>
                <Layers className="h-4 w-4 mr-2" />
                Create Protocol
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs for My Protocols and Templates */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="my-protocols">My Protocols</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template, index) => {
              const Icon = TEMPLATE_ICONS[template.template_category] || Layers;
              return (
                <Card key={index} className="p-6 glass-card hover:border-primary/50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <span className="px-2 py-1 rounded text-xs bg-primary/20 text-primary capitalize">
                      {template.template_category?.replace('_', ' ')}
                    </span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{template.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{template.description}</p>
                  
                  <div className="space-y-1 mb-4">
                    {template.peptides?.slice(0, 3).map((p, i) => (
                      <p key={i} className="text-xs text-muted-foreground">
                        • {p.peptide_name}: {p.dose_amount} {p.dose_unit}
                      </p>
                    ))}
                    {template.peptides?.length > 3 && (
                      <p className="text-xs text-muted-foreground">
                        + {template.peptides.length - 3} more...
                      </p>
                    )}
                  </div>

                  <Button 
                    className="w-full" 
                    onClick={() => handleImportTemplate(template.name)}
                    data-testid={`import-${template.template_category}`}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Use Template
                  </Button>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="my-protocols" className="mt-6">
          {protocols.length === 0 ? (
            <Card className="p-12 text-center glass-card">
              <Layers className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">No Protocols Yet</h2>
              <p className="text-muted-foreground mb-6">
                Create protocols or import from templates to get started.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => setShowDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Custom
                </Button>
                <Button variant="outline" onClick={() => setActiveTab('templates')}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Browse Templates
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {protocols.map(protocol => (
                <Card key={protocol.protocol_id} className="p-6 glass-card" data-testid={`protocol-${protocol.protocol_id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-xl">{protocol.name}</h3>
                      {protocol.description && (
                        <p className="text-sm text-muted-foreground mt-1">{protocol.description}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${protocol.is_active ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'}`}>
                      {protocol.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    {protocol.peptides?.map((p, index) => (
                      <div key={index} className="flex items-center justify-between p-2 rounded bg-secondary/50">
                        <span className="font-medium text-sm">{p.peptide_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {p.dose_amount} {p.dose_unit} • {p.timing}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      Created {new Date(protocol.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleShare(protocol)} title="Share">
                        <Share2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDuplicate(protocol)} title="Duplicate">
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(protocol.protocol_id)} title="Delete">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
